import {
  AvailableVariantT,
  DeliveryEstimateT,
  DeliveryType,
  ProductVariantT,
  SkuVariantT,
} from '@lambda/apis/src/catalog-v2/types';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/es';
import 'dayjs/locale/sv';
import 'dayjs/locale/nl';

import { groupBy } from 'lodash-es';
import {
  EU_STORES,
  getCurrentTimeZoneByStore,
  ReebeloConditions,
} from '@lambda/reebelo';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import i18n from '@/settings/i18n';
import { formatWarrantyInfo } from '@/lib/warranty';
import { findDeliveryEstimate } from '@/lib/delivery-estimates';
import { fitPageTitle } from '@/lib/page-title';
import {
  formatStoreDateRange,
  formatStoreDecimal,
  formatStoreNumber,
} from '@/lib/intl-format';
import { VariantTreeGetT } from './types';
import settings from '@/settings';
import auFlag from '@/public/icons/country-flags/AU.svg';
import nzFlag from '@/public/icons/country-flags/NZ.svg';
import sgFlag from '@/public/icons/country-flags/SG.svg';
import usFlag from '@/public/icons/country-flags/US.svg';
import caFlag from '@/public/icons/country-flags/CA.svg';
import ukFlag from '@/public/icons/country-flags/UK.svg';
import frFlag from '@/public/icons/country-flags/FR.svg';
import deFlag from '@/public/icons/country-flags/DE.svg';
import esFlag from '@/public/icons/country-flags/ES.svg';
import itFlag from '@/public/icons/country-flags/IT.svg';
import seFlag from '@/public/icons/country-flags/SE.svg';
import ieFlag from '@/public/icons/country-flags/IE.svg';
import nlFlag from '@/public/icons/country-flags/NL.svg';
import atFlag from '@/public/icons/country-flags/AT.svg';
import beFlag from '@/public/icons/country-flags/BE.svg';
import ptFlag from '@/public/icons/country-flags/PT.svg';
import fiFlag from '@/public/icons/country-flags/FI.svg';
import euFlag from '@/public/icons/country-flags/EU.svg';

export function formatOrigin(isLocalVendor: boolean): {
  origin: string;
  flag: any;
} {
  if (!isLocalVendor) {
    // In EU markets even non-local vendors ship from within the EU, so show
    // the EU flag rather than nothing. Other markets show no flag.
    if (EU_STORES.includes(settings.store)) {
      return { origin: 'in the EU', flag: euFlag };
    }

    return { origin: '', flag: null };
  }

  switch (settings.store) {
    case 'reebelo-us':
      return { flag: usFlag, origin: 'in the US' };
    case 'reebelo-nz':
      return { flag: nzFlag, origin: 'in New Zealand' };
    case 'quista':
      return { flag: sgFlag, origin: 'in Singapore' };
    case 'reebelo-au':
      return { flag: auFlag, origin: 'in Australia' };
    case 'reebelo-ca':
      return { flag: caFlag, origin: 'in Canada' };
    case 'reebelo-uk':
      return { flag: ukFlag, origin: 'in the UK' };
    case 'reebelo-fr':
      return { flag: frFlag, origin: 'in France' };
    case 'reebelo-de':
      return { flag: deFlag, origin: 'in Germany' };
    case 'reebelo-es':
      return { flag: esFlag, origin: 'in Spain' };
    case 'reebelo-it':
      return { flag: itFlag, origin: 'in Italy' };
    case 'reebelo-se':
      return { flag: seFlag, origin: 'in Sweden' };
    case 'reebelo-ie':
      return { flag: ieFlag, origin: 'in Ireland' };
    case 'reebelo-nl':
      return { flag: nlFlag, origin: 'in the Netherlands' };
    case 'reebelo-at':
      return { flag: atFlag, origin: 'in Austria' };
    case 'reebelo-be':
      return { flag: beFlag, origin: 'in Belgium' };
    case 'reebelo-pt':
      return { flag: ptFlag, origin: 'in Portugal' };
    case 'reebelo-fi':
      return { flag: fiFlag, origin: 'in Finland' };
    case 'reebelo-dev':
      return { flag: sgFlag, origin: 'in Reebelo' };
    default:
      return { flag: null, origin: '' };
  }
}

/**
 * Flag for a country, keyed on its ISO 3166-1 alpha-2 code. Every Reebelo
 * market country has an asset. A vendor registered anywhere else has none, and
 * callers then show the country name on its own rather than a flag naming a
 * different country.
 */
const COUNTRY_FLAGS: Record<string, any> = {
  AU: auFlag,
  CA: caFlag,
  DE: deFlag,
  ES: esFlag,
  FR: frFlag,
  GB: ukFlag,
  IT: itFlag,
  NZ: nzFlag,
  SE: seFlag,
  IE: ieFlag,
  NL: nlFlag,
  AT: atFlag,
  BE: beFlag,
  PT: ptFlag,
  FI: fiFlag,
  SG: sgFlag,
  US: usFlag,
};

export const getCountryFlag = (countryCode: string | null | undefined) =>
  (countryCode && COUNTRY_FLAGS[countryCode.toUpperCase()]) || null;

const generateSelection = (variants: SkuVariantT[] | undefined = []) => {
  const selection: Record<string, string> = {};

  variants.forEach((v) => {
    selection[v.variant.slug] = v.value.value;
  });

  return selection;
};

// TODO: Review and optimize this class further
export class VariantTree {
  private readonly productVariants: string[];

  private readonly tree: Record<string, AvailableVariantT>;

  private readonly paths: string[];

  constructor(
    availableVariants: AvailableVariantT[],
    variants: ProductVariantT[],
  ) {
    this.productVariants = variants.map((v) => v.variant.slug);

    const tree: Record<string, AvailableVariantT> = Object.create(null);
    const paths: string[] = [];

    // Available Variants is pre-sorted by price (cheapest first)
    availableVariants.forEach((variant) => {
      const selection = generateSelection(variant.variants);
      const path = this.toPath(selection);

      tree[path] = variant;
      paths.push(path);
    });

    this.tree = tree;
    this.paths = paths;
  }

  public readonly get = (
    variants: SkuVariantT[],
    nextSelection?: SkuVariantT[],
  ): VariantTreeGetT => {
    if (this.paths.length === 0) {
      return {
        skuId: '',
        shouldHide: true,
        price: 0,
      };
    }

    const selection = generateSelection(variants);

    // Remove batteryHealth from current selection to make sure we
    // always end up on the cheapest option within the selected variants
    delete selection.batteryHealth;

    const nextSel = generateSelection(nextSelection);

    // 1️⃣ Exact match first
    const exactPath = this.toPath({ ...selection, ...nextSel });
    const exactSku = this.tree[exactPath];

    if (exactSku) {
      return {
        skuId: exactSku.skuId,
        shouldHide: false,
        price: exactSku.price / 100,
      };
    }

    //  Battery selection should never change other variants
    if (nextSel.batteryHealth) {
      return {
        skuId: '',
        shouldHide: true,
        price: 0,
      };
    }

    // 2️⃣ Variants are displayed in priority order (`productVariants`). The
    //    option the user is selecting is the first entry of `nextSelection`;
    //    the rest are pinned context (e.g. cpu/carrier). Selecting an option
    //    may freely adjust LOWER-priority variants, but must never change a
    //    HIGHER-priority (earlier-displayed) one — if it would, we hide it.
    const targetSlug = nextSelection?.[0]?.variant.slug;
    const targetIndex = targetSlug
      ? this.productVariants.indexOf(targetSlug)
      : -1;
    const targetValue = targetSlug ? nextSel[targetSlug] : undefined;

    // The value the user has requested for a variant: a pinned value from
    // nextSelection, otherwise their current selection.
    const requestedValue = (slug: string) => nextSel[slug] ?? selection[slug];

    // Offers that provide the exact option the user is selecting (price-sorted).
    const optionPaths = this.paths.filter((pathKey) => {
      if (targetIndex < 0) {
        return true;
      }

      return pathKey.split(',')[targetIndex] === targetValue;
    });

    const higherVariants = this.productVariants.slice(
      0,
      Math.max(targetIndex, 0),
    );

    // Among the offers for this option, keep only those that preserve every
    // higher-priority variant at its requested value; then pick the one that
    // preserves the most of the rest of the selection (cheapest wins ties,
    // since `paths` is price-sorted). If none qualify, the option is hidden.
    let chosenPath: string | undefined;
    let bestMatches = -1;

    optionPaths.forEach((pathKey) => {
      const parts = pathKey.split(',');

      const preservesHigher = higherVariants.every((slug, idx) => {
        const req = requestedValue(slug);

        // RT-16750: a SKU with no value for this variant has '' here. That is
        // an absence, not a conflicting value, so it must not exclude the offer
        // — it simply scores lower than a genuine match below.
        return req == null || parts[idx] === '' || parts[idx] === req;
      });

      if (!preservesHigher) {
        return;
      }

      const matches = this.productVariants.reduce((acc, slug, idx) => {
        const req = requestedValue(slug);

        return acc + (req != null && parts[idx] === req ? 1 : 0);
      }, 0);

      if (matches > bestMatches) {
        bestMatches = matches;
        chosenPath = pathKey;
      }
    });

    // Fallback for the navigation target when the option can't be satisfied
    // without touching a higher-priority variant (in which case it's hidden).
    const chosen =
      (chosenPath && this.tree[chosenPath]) ||
      (optionPaths[0] && this.tree[optionPaths[0]]) ||
      this.tree[this.paths[0]];

    // 3️⃣ Result — never suggest (lower-priority adjustments are silent);
    //    hidden when reaching this option would change a higher-priority variant
    //    (or no offer provides it at all).
    return {
      skuId: chosen.skuId,
      shouldHide: !chosenPath,
      price: chosen.price / 100,
    };
  };

  public readonly getBatteryHealthOptions = (
    variants: SkuVariantT[],
  ): Record<string, VariantTreeGetT> => {
    const selection = generateSelection(variants);

    // Ignore any incoming batteryHealth — we want all of them for this combo.
    delete selection.batteryHealth;

    const result: Record<string, VariantTreeGetT> = {};

    this.paths.forEach((pathKey) => {
      const sku = this.tree[pathKey];

      if (!sku) {
        return;
      }

      const skuSelection = generateSelection(sku.variants);

      // Every non-battery slug in the selection must match this SKU.
      const matchesCore = Object.keys(selection).every(
        (slug) => skuSelection[slug] === selection[slug],
      );

      if (!matchesCore) {
        return;
      }

      const batteryHealth = sku.variants.find(
        (v) => v.variant.slug === 'batteryHealth',
      );

      if (batteryHealth && !result[batteryHealth.value.value]) {
        result[batteryHealth.value.value] = this.get(variants, [batteryHealth]);
      }
    });

    return result;
  };

  private toPath(selection: Record<string, string>): string {
    const parts = new Array(this.productVariants.length);

    for (let i = 0; i < this.productVariants.length; i += 1) {
      const slug = this.productVariants[i];

      parts[i] = selection[slug] ?? '';
    }

    return parts.join(',');
  }
}

export const generateDeliveryDateObjects = (
  deliveryEstimates: DeliveryEstimateT[] | undefined = [],
  destinationState: string | null = null,
  destinationRegion: string | null = null,
) => {
  const findDelivery = (type: DeliveryType) =>
    findDeliveryEstimate(
      deliveryEstimates,
      type,
      destinationState,
      destinationRegion,
    );

  const standardDelivery = findDelivery(DeliveryType.STANDARD);
  const expressDelivery = findDelivery(DeliveryType.EXPRESS);

  const getMinMaxDates = (delivery?: DeliveryEstimateT) => {
    if (!delivery) {
      return { min: null, max: null };
    }

    // Prefer pre-computed date strings from the server
    if (delivery.minDate && delivery.maxDate) {
      return { min: delivery.minDate, max: delivery.maxDate };
    }

    // Fallback: derive from legacy day counts using the store's local timezone
    const tz = getCurrentTimeZoneByStore(settings.store);
    const today = dayjs().tz(tz);
    const min =
      delivery.minDays != null
        ? today.add(delivery.minDays, 'day').format('YYYY-MM-DD')
        : null;
    const max =
      delivery.maxDays != null
        ? today.add(delivery.maxDays, 'day').format('YYYY-MM-DD')
        : null;

    return { min, max };
  };

  const { min: standardMinDate, max: standardMaxDate } =
    getMinMaxDates(standardDelivery);
  const { min: expressMinDate, max: expressMaxDate } =
    getMinMaxDates(expressDelivery);

  return {
    standardMinDate,
    standardMaxDate,
    expressMinDate,
    expressMaxDate,
    standardCutoffDeadline: standardDelivery?.cutoffDeadline ?? null,
  };
};

/**
 * A delivery window as the PDP strip and the cart show it ("Oct 20 - 21").
 *
 * French, Italian, Spanish, German, Swedish and Dutch write the day before the
 * month ("20 oct.", "20. Okt.", "20 okt"); German puts a period after the day.
 * Spanish reads as a sentence, so its connectors live here rather than in the
 * label: "entre el 2 y el 3 de sep" or "el 2 de sep".
 */
export const formatDeliveryRange = (
  minDate: string | null,
  maxDate: string | null,
  { format, locale }: { format: 'short' | 'long'; locale: string },
): string | null => {
  if (!maxDate) {
    return null;
  }

  // EU stores: Intl in the store's locale ("6.–7. Okt.", "6–7 oct.").
  const storeRange = formatStoreDateRange(
    dayjs(minDate ?? maxDate),
    dayjs(maxDate),
    format === 'long' ? 'dayMonth' : 'dayMonthShort',
  );

  if (storeRange) {
    return storeRange;
  }

  const isSpanish = locale === 'es';
  const isDayFirst = ['fr', 'it', 'es', 'de', 'sv', 'nl'].includes(locale);
  const dayToken = locale === 'de' ? 'D.' : 'D';

  const minDateObj = minDate ? dayjs(minDate).locale(locale) : null;
  const maxDateObj = dayjs(maxDate).locale(locale);

  const monthToken = format === 'long' ? 'MMMM' : 'MMM';
  let fullFormat = `${monthToken} D`;

  if (isSpanish) {
    fullFormat = `D [de] ${monthToken}`;
  } else if (isDayFirst) {
    fullFormat = `${dayToken} ${monthToken}`;
  }

  const isSameMonth = minDateObj && minDateObj.month() === maxDateObj.month();

  const single = (date: dayjs.Dayjs) =>
    isSpanish ? `el ${date.format(fullFormat)}` : date.format(fullFormat);

  if (!minDateObj || minDateObj.isSame(maxDateObj, 'day')) {
    return single(maxDateObj);
  }

  // When the range stays within one month, the shared month name sits on the
  // side closest to it: trailing in English, leading in day-first locales.
  const minDateFormatted = minDateObj.format(
    isDayFirst && isSameMonth ? dayToken : fullFormat,
  );

  const maxDateFormatted = maxDateObj.format(
    !isDayFirst && isSameMonth ? 'D' : fullFormat,
  );

  return isSpanish
    ? `entre el ${minDateFormatted} y el ${maxDateFormatted}`
    : `${minDateFormatted} - ${maxDateFormatted}`;
};

export const generateFormattedDeliveryDates = ({
  deliveryEstimates = [],
  format = 'short',
  destinationState,
  destinationRegion,
}: {
  deliveryEstimates: DeliveryEstimateT[] | undefined;
  format?: 'short' | 'long';
  destinationState?: string;
  destinationRegion?: string;
}) => {
  const {
    standardMinDate,
    standardMaxDate,
    expressMinDate,
    expressMaxDate,
    standardCutoffDeadline,
  } = generateDeliveryDateObjects(
    deliveryEstimates,
    destinationState ?? null,
    destinationRegion ?? null,
  );

  const locale = i18n.getLocale();
  const formatDateRange = (minDate: string | null, maxDate: string | null) =>
    formatDeliveryRange(minDate, maxDate, { format, locale });

  const formattedStandardDelivery = formatDateRange(
    standardMinDate,
    standardMaxDate,
  );

  const formattedExpressDelivery = formatDateRange(
    expressMinDate,
    expressMaxDate,
  );

  return {
    formattedStandardDelivery,
    formattedExpressDelivery,
    standardCutoffDeadline,
  };
};

export const generateSavingsPercentage = (
  price: number,
  compareAtPrice: number | null,
) => {
  if (!compareAtPrice || !price) {
    return null;
  }

  const percentage = Math.round(
    ((compareAtPrice - price) / compareAtPrice) * 100,
  );

  if (percentage > 3) {
    return percentage;
  }

  return null;
};

/**
 * The share of good reviews as it reads in copy: one decimal, and no trailing
 * ".0" so a spotless seller shows "100%" rather than "100.0%". Returns null
 * when there is no figure to show, so callers can drop the line entirely.
 */
export const formatGoodReviewsPercentage = (
  percentage: number | null,
): string | null => {
  if (percentage == null || !Number.isFinite(percentage)) {
    return null;
  }

  // "98,5" on the EU stores.
  return formatStoreDecimal(Number(percentage.toFixed(1)));
};

export const generateUnitsSoldCount = (count: number | null) => {
  if (!count || (count && count < 125)) {
    return '125+';
  }

  return `${formatStoreNumber(count)}+`;
};

/**
 * Collapses SKUs that share a variant combination (keeping the first listed —
 * the API returns them cheapest first) and sorts the result by price.
 *
 * Duplicate hiding — a worse condition or battery health priced at or above a
 * better one — is no longer done here. MsOffer2 owns that decision and the
 * catalog drops hidden offers before they reach the storefront (RT-17370), so
 * this no longer re-applies its own rules to fee-inclusive prices.
 */
export const streamlineAvailableVariants = (
  variants: AvailableVariantT[],
  productVariants: ProductVariantT[],
): AvailableVariantT[] => {
  const orderedProductVariants = productVariants.map((v) => v.variant.slug);

  // Build joined key for each offer
  const offersWithKeys = variants.map((offer) => {
    const map = Object.fromEntries(
      offer.variants.map((v) => [v.variant.slug, v.value.value]),
    );

    const key = orderedProductVariants.map((slug) => map[slug] ?? '').join(',');

    return { key, offer };
  });

  // Group by the combined key
  const grouped = groupBy(offersWithKeys, 'key');

  // Take only the first offer in each group
  const deduped = Object.values(grouped).map((items) => items[0].offer);

  return deduped.sort((a, b) => {
    const pa = a.price ?? Number.POSITIVE_INFINITY;
    const pb = b.price ?? Number.POSITIVE_INFINITY;

    return pa - pb;
  });
};

export const generateCategoryWarranty = ({
  condition,
  categoryWarrantyInfo,
}: {
  condition: string;
  categoryWarrantyInfo: ProductCategoryWarranty | null;
}) => {
  const isRefurbishedAndSG =
    settings.store === 'quista' && condition !== ReebeloConditions.BrandNew;

  if (isRefurbishedAndSG) {
    return '3 Months';
  }

  return (
    formatWarrantyInfo(categoryWarrantyInfo) ||
    `${settings.warranty_month} Months`
  );
};

/**
 * RT-16298 / RT-16875: a product is an accessory when any category in its
 * breadcrumb chain is named "...Accessories" / "...Accessory". Shared by the
 * cart line-item type and the PDP <title>, so both agree on the definition.
 */
export const isAccessoryBreadcrumb = (
  breadcrumb: { name: string }[] | null | undefined,
): boolean =>
  (breadcrumb ?? []).some((b) => {
    const name = b.name.toLowerCase();

    return name.includes('accessories') || name.includes('accessory');
  });

const t = i18n.init('product');

/**
 * RT-16875: the PDP <title> (also reused for og:title / twitter:title).
 * Brand-new stock and accessories, which are sold new/open-box, are never
 * called "Refurbished"; every other condition is. RT-17612: the site name is
 * dropped when the title would be too long for Google to show in full.
 */
export const buildProductPageTitle = ({
  productName,
  condition,
  breadcrumb,
  country,
}: {
  productName: string;
  condition: string;
  breadcrumb: { name: string }[] | null | undefined;
  country: string;
}): string => {
  const showRefurbishedPrefix =
    condition !== ReebeloConditions.BrandNew &&
    !isAccessoryBreadcrumb(breadcrumb);

  return fitPageTitle(
    showRefurbishedPrefix
      ? t`Refurbished ${{ productName }} | Reebelo ${{ country }}`
      : t`${{ productName }} | Reebelo ${{ country }}`,
  );
};
